import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { BlockEditModal } from "../BlockEditModal";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import {
  IconInfoCircle,
  IconBulb,
  IconAlertTriangle,
  IconAlertCircle,
  IconBook,
} from "@tabler/icons-react";
import type { Icon } from "@tabler/icons-react";
import type { CalloutType } from "@/lib/lilia-stubs/types";
import styles from "../blocks/CalloutBlock.module.scss";

const CALLOUT_TYPES: CalloutType[] = ["note", "tip", "warning", "important", "example"];

const CALLOUT_CONFIG: Record<CalloutType, { label: string; icon: Icon; color: string }> = {
  note: { label: "Note", icon: IconInfoCircle, color: "#2563eb" },
  tip: { label: "Tip", icon: IconBulb, color: "#16a34a" },
  warning: { label: "Warning", icon: IconAlertTriangle, color: "#d97706" },
  important: { label: "Important", icon: IconAlertCircle, color: "#dc2626" },
  example: { label: "Example", icon: IconBook, color: "#9333ea" },
};

export function CalloutNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const rawType = node.attrs.calloutType as string;
  const calloutType: CalloutType = CALLOUT_TYPES.includes(rawType as CalloutType)
    ? (rawType as CalloutType)
    : "note";
  const title = (node.attrs.title as string) || "";
  const text = (node.attrs.text as string) || "";
  const collapsible = (node.attrs.collapsible as boolean) ?? false;
  const color = (node.attrs.color as string) || "";

  const config = CALLOUT_CONFIG[calloutType];
  const CalloutIcon = config.icon;
  // Free-form color override (PR 4 — Text block / colored tcolorbox).
  // When empty, fall back to the variant's preset color.
  const effectiveColor = color || config.color;

  const [isOpen, setIsOpen] = useState(false);
  const [draftType, setDraftType] = useState<CalloutType>(calloutType);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftText, setDraftText] = useState(text);
  const [draftCollapsible, setDraftCollapsible] = useState(collapsible);
  const [draftColor, setDraftColor] = useState(color);
  const [collapsed, setCollapsed] = useState(false);

  const handleOpen = useCallback(() => {
    setDraftType(calloutType);
    setDraftTitle(title);
    setDraftText(text);
    setDraftCollapsible(collapsible);
    setDraftColor(color);
    setIsOpen(true);
  }, [calloutType, title, text, collapsible, color]);

  const handleSave = useCallback(() => {
    updateAttributes({
      calloutType: draftType,
      title: draftTitle,
      text: draftText,
      collapsible: draftCollapsible,
      color: draftColor,
    });
    setIsOpen(false);
  }, [draftType, draftTitle, draftText, draftCollapsible, draftColor, updateAttributes]);

  const displayTitle = title || config.label;

  return (
    <NodeViewWrapper data-type="callout-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={`${styles.calloutBlock} ${styles[`callout-${calloutType}`]}`}
        style={{ borderLeftColor: effectiveColor }}
        onClick={handleOpen}
        title="Click to edit callout"
      >
        <div className={styles.header}>
          <CalloutIcon size={18} style={{ color: effectiveColor, flexShrink: 0 }} />
          <span className={styles.title} style={{ color: effectiveColor }}>{displayTitle}</span>
          {collapsible && (
            <button
              className={styles.collapseToggle}
              onClick={(e) => { e.stopPropagation(); setCollapsed(!collapsed); }}
              title={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? "+" : "-"}
            </button>
          )}
        </div>
        {(!collapsible || !collapsed) && (
          text ? (
            <div
              className={styles.content}
              dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }}
            />
          ) : (
            <div className={styles.content}>
              <span className={styles.emptyHint}>Click to add callout content...</span>
            </div>
          )
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title={`Edit ${CALLOUT_CONFIG[draftType].label} Callout`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={640}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
          <Select value={draftType} onValueChange={(val) => setDraftType((val || "note") as CalloutType)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CALLOUT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{CALLOUT_CONFIG[type].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Title (optional)</label>
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder={`e.g., ${CALLOUT_CONFIG[draftType].label}`}
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Content</label>
          <textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder="Enter callout content..."
            rows={6}
            autoFocus
            style={{
              width: "100%", padding: "12px", fontSize: 13,
              border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              resize: "vertical", lineHeight: 1.6,
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Color override (optional)
          </label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {["", "red", "orange", "amber", "yellow", "lime", "green", "teal", "cyan", "sky", "blue", "indigo", "violet", "purple", "pink", "rose", "gray", "brown"].map((c) => (
              <button
                key={c || "default"}
                type="button"
                onClick={() => setDraftColor(c)}
                title={c || "Variant default"}
                style={{
                  width: 22, height: 22, borderRadius: 4,
                  border: draftColor === c ? "2px solid var(--lilia-primary, #3b82f6)" : "1px solid var(--lilia-border, #ddd)",
                  background: c || "linear-gradient(135deg, transparent 45%, #999 45%, #999 55%, transparent 55%)",
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            ))}
            <input
              type="text"
              value={draftColor}
              onChange={(e) => setDraftColor(e.target.value)}
              placeholder="Or type: e.g. ForestGreen"
              style={{
                marginLeft: 8, flex: 1, minWidth: 140, padding: "6px 10px",
                fontSize: 12, border: "1px solid var(--lilia-border, #ddd)",
                borderRadius: 4,
              }}
            />
          </div>
          <p style={{ marginTop: 4, fontSize: 11, color: "var(--lilia-text-muted, #888)" }}>
            Free-form color name (xcolor / CSS — red, blue, ForestGreen, …).
            Leave first swatch picked to use the type's default color.
          </p>
        </div>

        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={draftCollapsible}
              onChange={(e) => setDraftCollapsible(e.target.checked)}
            />
            Collapsible
          </label>
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
